/**
 * LinearScale authoring controls (start/end bounds + optional end captions), rendered in the V2
 * question card. Self-gating: renders nothing for NPS (fully standardized) or any non-scale type.
 * Writes the scale config onto the question's RHF subtree, which round-trips via `surveyForm`.
 */
import { css } from 'aphrodite';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Input } from '../../../../../../../components-v2/Input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../../../../../../components-v2/Select';
import { QuestionType } from '../../../FormFieldWithOptions/questionTypes';
import { styleSheet } from './ScaleQuestionSettings.styles';

export interface ScaleQuestionSettingsProps {
	/** RHF field name for the question, e.g. "surveyForm.surveyParts.2.questions.1". */
	questionFieldNamePrefix: string;
}

// LinearScale bounds permitted by the BE (LinearScaleQuestion.Validate): start ∈ {0,1}, end ∈ [2,10].
const LINEAR_SCALE_START_OPTIONS = [0, 1];
const LINEAR_SCALE_END_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export const ScaleQuestionSettings: React.FC<ScaleQuestionSettingsProps> = ({ questionFieldNamePrefix }) => {
	const { control, setValue } = useFormContext();

	const questionType = useWatch({ control, name: `${questionFieldNamePrefix}._type` }) as QuestionType | undefined;
	const minValue = useWatch({ control, name: `${questionFieldNamePrefix}.minValue` }) as number | undefined;
	const maxValue = useWatch({ control, name: `${questionFieldNamePrefix}.maxValue` }) as number | undefined;
	const startLabel = useWatch({ control, name: `${questionFieldNamePrefix}.startLabel` }) as string | null | undefined;
	const endLabel = useWatch({ control, name: `${questionFieldNamePrefix}.endLabel` }) as string | null | undefined;

	const setField = React.useCallback(
		(key: 'minValue' | 'maxValue' | 'startLabel' | 'endLabel', value: number | string | null) => {
			setValue(`${questionFieldNamePrefix}.${key}`, value, { shouldDirty: true });
		},
		[questionFieldNamePrefix, setValue]
	);

	if (questionType !== QuestionType.LinearScale) return null;

	// Empty captions stored as null so they're omitted server-side.
	const onLabelChange = (key: 'startLabel' | 'endLabel') => (e: React.ChangeEvent<HTMLInputElement>) =>
		setField(key, e.target.value === '' ? null : e.target.value);

	const rows = [
		{
			boundLabel: 'Scale starts at',
			boundValue: String(minValue ?? 1),
			boundOptions: LINEAR_SCALE_START_OPTIONS,
			onBoundChange: (val: string) => setField('minValue', Number(val)),
			captionValue: startLabel ?? '',
			captionPlaceholder: 'e.g. Not satisfied',
			onCaptionChange: onLabelChange('startLabel'),
		},
		{
			boundLabel: 'Scale ends at',
			boundValue: String(maxValue ?? 5),
			boundOptions: LINEAR_SCALE_END_OPTIONS,
			onBoundChange: (val: string) => setField('maxValue', Number(val)),
			captionValue: endLabel ?? '',
			captionPlaceholder: 'e.g. Very satisfied',
			onCaptionChange: onLabelChange('endLabel'),
		},
	];

	return (
		<div className={css(styleSheet.container)}>
			{rows.map(row => (
				<div key={row.boundLabel} className={css(styleSheet.row)}>
					<div className={css(styleSheet.field, styleSheet.scaleSelectField)}>
						<label className={css(styleSheet.fieldLabel)}>{row.boundLabel}</label>
						<Select value={row.boundValue} onValueChange={row.onBoundChange}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{row.boundOptions.map(n => (
									<SelectItem key={n} value={String(n)}>
										{n}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className={css(styleSheet.field)}>
						<label className={css(styleSheet.fieldLabel)}>Label (optional)</label>
						<Input value={row.captionValue} placeholder={row.captionPlaceholder} onChange={row.onCaptionChange} />
					</div>
				</div>
			))}
		</div>
	);
};

ScaleQuestionSettings.displayName = 'ScaleQuestionSettings';
